import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageHeader from "../karkhana/ui/PageHeader";
import { PartnerRow, createProductionOrder, listPartners } from "../../services/partnerNetworkApi";
import { DesignFiles, ShownRef } from "./pnUi";
import "../karkhana/ui/karkhana-ui.scss";
import "./partner-network.scss";

type Line = { colour: string; quantity: string; size: string };

const NewProductionOrderPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [form, setForm] = useState({
    partnershipId: new URLSearchParams(location.search).get("partner") || "",
    title: "",
    productType: "",
    specifications: "",
    dueDate: "",
    proposedRate: "",
    notes: "",
    clientName: "",
    sellingPrice: "",
    margin: "",
  });
  const [designRefs, setDesignRefs] = useState<ShownRef[]>([]);
  const [lines, setLines] = useState<Line[]>([{ colour: "", quantity: "", size: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [createdId, setCreatedId] = useState("");

  useEffect(() => {
    listPartners().then((res) => {
      if (res.success) setPartners(res.data.partners.filter((p) => p.status === "ACTIVE"));
    });
  }, []);

  const set = (k: string, v: string) => setForm({ ...form, [k]: v });

  const submit = async () => {
    setBusy(true);
    setError("");
    const res = await createProductionOrder({
      partnershipId: form.partnershipId,
      title: form.title,
      productType: form.productType,
      specifications: form.specifications,
      dueDate: form.dueDate || undefined,
      proposedRate: form.proposedRate ? Number(form.proposedRate) : undefined,
      notes: form.notes,
      designRefs: designRefs.map((r) => ({ key: r.key, name: r.name, contentType: r.contentType })),
      lines: lines
        .filter((l) => l.colour || l.quantity || l.size)
        .map((l) => ({ colour: l.colour, size: l.size, quantity: l.quantity ? Number(l.quantity) : undefined })),
      principalPrivate: {
        clientName: form.clientName || undefined,
        sellingPrice: form.sellingPrice ? Number(form.sellingPrice) : undefined,
        margin: form.margin ? Number(form.margin) : undefined,
      },
    });
    setBusy(false);
    if (!res.success) {
      setError(res.message || "Could not send the order");
      return;
    }
    if (res.data.linkUrl) {
      setLinkUrl(res.data.linkUrl);
      setCreatedId(res.data.order.id);
    } else {
      history.replace(`/partner-network/orders/${res.data.order.id}`);
    }
  };

  if (linkUrl) {
    return (
      <div className="pn-page">
        <PageHeader title="Order sent" back="/partner-network/board" />
        <div className="pn-card">
          <p>We sent the order link by SMS. You can also share it on WhatsApp:</p>
          <p className="pn-note" style={{ wordBreak: "break-all" }}>{linkUrl}</p>
          <div className="pn-actions">
            <button className="kh-btn" onClick={() => navigator.clipboard?.writeText(linkUrl)}>Copy link</button>
            <button className="kh-btn is-primary" onClick={() => history.replace(`/partner-network/orders/${createdId}`)}>Open order</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pn-page">
      <PageHeader title="New production order" back="/partner-network/board" />
      <div className="pn-card">
        <h3>Order</h3>
        <div className="pn-form">
          <label className="is-wide">
            Partner
            <select value={form.partnershipId} onChange={(e) => set("partnershipId", e.target.value)}>
              <option value="">Choose a partner</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="is-wide">
            Title
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Jamdani saree, indigo" />
          </label>
          <label>
            Product type
            <input value={form.productType} onChange={(e) => set("productType", e.target.value)} />
          </label>
          <label>
            Due date
            <input type="date" value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} />
          </label>
          <label>
            Rate per piece (₹)
            <input inputMode="decimal" value={form.proposedRate} onChange={(e) => set("proposedRate", e.target.value)} />
          </label>
          <label className="is-wide">
            Specifications
            <textarea rows={3} value={form.specifications} onChange={(e) => set("specifications", e.target.value)} />
          </label>
          <label className="is-wide">Note to partner<textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></label>
        </div>
      </div>
      <div className="pn-card">
        <h3>Design files</h3>
        <p className="pn-muted">Sketches, motif photos or a tech pack (images or PDF, up to 12). The partner sees these with the order.</p>
        <DesignFiles refs={designRefs} onChange={setDesignRefs} />
      </div>
      <div className="pn-card">
        <h3>Lines</h3>
        {lines.map((line, i) => (
          <div className="pn-form" key={i} style={{ marginBottom: 8 }}>
            <label>Colour<input value={line.colour} onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, colour: e.target.value } : l)))} /></label>
            <label>Quantity<input inputMode="numeric" value={line.quantity} onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, quantity: e.target.value } : l)))} /></label>
            <label>Size<input value={line.size} onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, size: e.target.value } : l)))} /></label>
          </div>
        ))}
        <button className="kh-btn" onClick={() => setLines([...lines, { colour: "", quantity: "", size: "" }])}>+ Add line</button>
      </div>
      <div className="pn-card">
        <h3>Only you see this</h3>
        <p className="pn-muted">Never shown to the partner.</p>
        <div className="pn-form">
          <label>Client<input value={form.clientName} onChange={(e) => set("clientName", e.target.value)} /></label>
          <label>Selling price (₹)<input inputMode="decimal" value={form.sellingPrice} onChange={(e) => set("sellingPrice", e.target.value)} /></label>
          <label>Margin (%)<input inputMode="decimal" value={form.margin} onChange={(e) => set("margin", e.target.value)} /></label>
        </div>
      </div>
      {error ? <p className="pn-limit">{error}</p> : null}
      <div className="pn-actions">
        <button className="kh-btn is-primary" disabled={busy || !form.partnershipId || !form.title} onClick={submit}>
          Send order
        </button>
      </div>
    </div>
  );
};

export default NewProductionOrderPage;
